import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, MessageSquare, Mail, Phone, Building, 
  Clock, CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { GlowingCard } from '../../components/3DEffects';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const res = await fetch(`${API}/admin/inquiries`, { credentials: 'include' });
      if (res.ok) {
        setInquiries(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    setUpdatingId(inquiryId);
    try {
      const res = await fetch(`${API}/admin/inquiries/${inquiryId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (res.ok) {
        setInquiries(inquiries.map(i => 
          i.inquiry_id === inquiryId ? { ...i, status: newStatus } : i
        ));
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = 
      inquiry.name?.toLowerCase().includes(search.toLowerCase()) ||
      inquiry.email?.toLowerCase().includes(search.toLowerCase()) ||
      inquiry.message?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      new: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
      contacted: 'text-blue-500 bg-blue-500/10 border-blue-500/30',
      converted: 'text-green-400 bg-green-400/10 border-green-400/30',
      closed: 'text-gray-400 bg-gray-400/10 border-gray-400/30'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-inquiries">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Contact Inquiries</h1>
        <p className="text-gray-400">Manage contact form submissions</p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row gap-4 mb-6"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inquiries..."
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent className="bg-[#0D1224] border-white/10">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {['new', 'contacted', 'converted', 'closed'].map((status) => {
          const count = inquiries.filter(i => i.status === status).length;
          return (
            <motion.div
              key={status}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`p-4 rounded-xl text-center ${getStatusColor(status)} border`}
            >
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-xs capitalize">{status}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Inquiries List */}
      {filteredInquiries.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No inquiries found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry, idx) => (
            <motion.div
              key={inquiry.inquiry_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
            >
              <GlowingCard className="p-5 rounded-xl">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-semibold">{inquiry.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {inquiry.email}
                      </span>
                      {inquiry.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {inquiry.phone}
                        </span>
                      )}
                      {inquiry.company && (
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {inquiry.company}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(inquiry.created_at)}
                  </span>
                </div>

                {inquiry.service_interest && (
                  <div className="mb-3">
                    <span className="px-2 py-1 rounded bg-white/5 text-xs text-gray-400">
                      Interest: {inquiry.service_interest}
                    </span>
                  </div>
                )}

                <p className="text-gray-400 text-sm mb-4 p-3 bg-white/5 rounded-lg">
                  {inquiry.message}
                </p>

                {/* Status Actions */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  {inquiry.status === 'new' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(inquiry.inquiry_id, 'contacted')}
                      disabled={updatingId === inquiry.inquiry_id}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                    >
                      {updatingId === inquiry.inquiry_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Mark Contacted'
                      )}
                    </Button>
                  )}
                  {inquiry.status === 'contacted' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(inquiry.inquiry_id, 'converted')}
                        disabled={updatingId === inquiry.inquiry_id}
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Converted
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(inquiry.inquiry_id, 'closed')}
                        disabled={updatingId === inquiry.inquiry_id}
                        className="bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border border-gray-500/30"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    </>
                  )}
                </div>
              </GlowingCard>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
